/**
 * Signaler une question d'exercice (migration 0348) et la corriger.
 *
 * - Élève / tout compte : `useReportQuestion` (insert PostgREST sous RLS, juste
 *   après avoir répondu). Admins + auteur de l'exercice prévenus (push + email,
 *   file regroupée) et le signalement apparaît dans leur Feed.
 * - Admin / auteur : lien vers l'éditeur V2 sur la question (`reportEditSearch`),
 *   statut traité / ignoré, et révision de la question par l'IA
 *   (`useReviseQuestion` → edge fn `exercise-v2-question-revise`, aperçu puis
 *   application via `exercise-blocks-mutate`).
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { SupabaseClient } from '@supabase/supabase-js';
import { supabase } from '@/lib/supabase';
import type { BlockMutationOp } from './types';

const db = supabase as unknown as SupabaseClient;

export type QuestionReportKind = 'error' | 'improvement';
export type QuestionReportStatus = 'open' | 'resolved' | 'dismissed';

export const QUESTION_REPORT_KIND_LABELS: Record<QuestionReportKind, string> = {
  error: 'Il y a une erreur',
  improvement: 'Une amélioration à proposer',
};

export const QUESTION_REPORT_STATUS_LABELS: Record<QuestionReportStatus, string> = {
  open: 'À traiter',
  resolved: 'Traité',
  dismissed: 'Ignoré',
};

export const QUESTION_REPORT_MESSAGE_MIN = 3;
export const QUESTION_REPORT_MESSAGE_MAX = 1000;

export interface QuestionReport {
  id: string;
  exercise_id: string;
  question_block_id: string | null;
  reporter_id: string;
  kind: QuestionReportKind;
  message: string;
  question_excerpt: string | null;
  status: QuestionReportStatus;
  created_at: string;
  resolved_at: string | null;
}

/** Message saisi : erreur à afficher, ou null s'il est valide. PUR. */
export function reportMessageIssue(message: string): string | null {
  const m = message.trim();
  if (m.length < QUESTION_REPORT_MESSAGE_MIN) return 'Explique en quelques mots ce qui ne va pas.';
  if (m.length > QUESTION_REPORT_MESSAGE_MAX)
    return `${QUESTION_REPORT_MESSAGE_MAX} caractères au maximum.`;
  return null;
}

/** Erreur d'enregistrement → message lisible. PUR. */
export function translateReportError(message: string): string {
  if (message.includes('report_rate_limited'))
    return 'Tu as déjà envoyé beaucoup de signalements aujourd’hui. Réessaie demain.';
  if (message.includes('report_question_not_in_exercise'))
    return 'Cette question n’appartient pas à l’exercice.';
  return 'Le signalement n’a pas pu être envoyé. Réessaie.';
}

/** Paramètres de l'éditeur V2 pour corriger la question signalée. PUR. */
export function reportEditSearch(report: { id: string; question_block_id: string | null }): {
  question?: string;
  report: string;
} {
  return report.question_block_id
    ? { question: report.question_block_id, report: report.id }
    : { report: report.id };
}

export function useReportQuestion() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      exerciseId: string;
      questionBlockId: string;
      attemptId?: string | null;
      kind: QuestionReportKind;
      message: string;
      userId: string;
    }) => {
      const { error } = await db.from('question_reports').insert({
        exercise_id: input.exerciseId,
        question_block_id: input.questionBlockId,
        attempt_id: input.attemptId ?? null,
        reporter_id: input.userId,
        kind: input.kind,
        message: input.message.trim(),
      });
      if (error) throw new Error(translateReportError(error.message));
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['news-question-reports'] }),
  });
}

export const questionReportQueryKey = (id: string) => ['question-report', id] as const;

export function useQuestionReport(reportId: string | undefined) {
  return useQuery({
    queryKey: questionReportQueryKey(reportId ?? ''),
    enabled: !!reportId,
    queryFn: async (): Promise<(QuestionReport & { reporter_name: string | null }) | null> => {
      const { data, error } = await db
        .from('question_reports')
        .select(
          'id, exercise_id, question_block_id, reporter_id, kind, message, question_excerpt, status, created_at, resolved_at, reporter:profiles!question_reports_reporter_id_fkey(display_name:visible_name)',
        )
        .eq('id', reportId as string)
        .maybeSingle();
      if (error) throw new Error(error.message);
      if (!data) return null;
      const row = data as unknown as QuestionReport & {
        reporter: { display_name: string | null } | null;
      };
      return { ...row, reporter_name: row.reporter?.display_name ?? null };
    },
  });
}

export function useSetReportStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, status }: { id: string; status: QuestionReportStatus }) => {
      const { error } = await db.from('question_reports').update({ status }).eq('id', id);
      if (error) throw new Error(error.message);
    },
    onSettled: (_d, _e, vars) => {
      qc.invalidateQueries({ queryKey: questionReportQueryKey(vars.id) });
      qc.invalidateQueries({ queryKey: ['news-question-reports'] });
    },
  });
}

/** Aperçu renvoyé par l'IA (miroir de `QuestionReviseOutput`). */
export interface QuestionRevisionPreview {
  prompt_md: string;
  sub_statement_md?: string | null;
  steps: {
    prompt_md?: string | null;
    answer_md: string;
    explanation_md: string;
    distractors: { label_md: string; feedback_md?: string | null; is_correct?: boolean }[];
  }[];
  summary: string;
}

const REVISE_ERROR_MESSAGES: Record<string, string> = {
  budget_exceeded: 'Le budget IA du jour est atteint. Réessaie demain.',
  question_not_found: 'Question introuvable (elle a peut-être été supprimée).',
  question_without_answer: 'Cette question n’a pas encore de réponse à réviser.',
  forbidden: 'Seuls l’auteur de l’exercice et les admins peuvent le modifier.',
  invalid_input: 'Écris tes recommandations (au moins quelques mots).',
};

export function useReviseQuestion() {
  return useMutation({
    mutationFn: async (input: {
      exerciseId: string;
      questionBlockId: string;
      instructions: string;
    }): Promise<{ ops: BlockMutationOp[]; preview: QuestionRevisionPreview }> => {
      const { data, error } = await supabase.functions.invoke<{
        ok: boolean;
        ops: BlockMutationOp[];
        preview: QuestionRevisionPreview;
      }>('exercise-v2-question-revise', {
        body: {
          exercise_id: input.exerciseId,
          question_block_id: input.questionBlockId,
          instructions: input.instructions,
        },
      });
      if (error) {
        let code: string | undefined;
        let message = error.message;
        const ctx = (error as Error & { context?: Response }).context;
        if (ctx && typeof ctx.json === 'function') {
          try {
            const parsed = (await ctx.json()) as { error?: string; message?: string };
            code = parsed.error;
            message = parsed.message ?? parsed.error ?? message;
          } catch {
            /* message générique */
          }
        }
        throw new Error((code && REVISE_ERROR_MESSAGES[code]) || message);
      }
      if (!data) throw new Error('Réponse vide du serveur.');
      return { ops: data.ops, preview: data.preview };
    },
  });
}

/** Recommandations proposées à l'IA depuis un signalement. PUR. */
export function reportInstructions(report: Pick<QuestionReport, 'kind' | 'message'>): string {
  const head =
    report.kind === 'improvement'
      ? 'Amélioration proposée par un élève'
      : 'Erreur signalée par un élève';
  return `${head} : « ${report.message.trim()} »`;
}
